// function-evidence — the plain per-case readout under a `function-io` row in the Check results
// (Trello DyXzvVbP). A student whose return-only function passed saw an EMPTY console and a pass
// they did not trust: nothing showed the function had run at all. This turns the evidence that
// `compareFunctionIO` (challenge-grading.js) already collects into short lines such as
//
//     double(5) returned 10 — expected 10 ✓
//     double(13) returned 25 — expected 26 ✗
//
// Pure and DOM-free: it returns TEXT, and challenge-chrome.js writes every line with
// textContent (never innerHTML — a returned value is the student's own data). Display-only:
// nothing here can change a verdict, and none of it is posted.

export var MAX_CASES = 5;
export var MAX_VALUE_CHARS = 40;
export var MAX_CALL_CHARS = 60;
export var MAX_TYPED_CHARS = 60;

export var PASS_MARK = '✓';
export var FAIL_MARK = '✗';

// Shorten a rendered value, keeping its start (the part a student recognises).
export function clip(text, max) {
  var s = String(text == null ? '' : text);
  if (s.length <= max) { return s; }
  return s.slice(0, Math.max(1, max - 1)) + '…';
}

function v(text) { return clip(text, MAX_VALUE_CHARS); }

// One case -> one line of text (with its tick / cross).
export function evidenceLine(ev) {
  ev = ev || {};
  var call = clip(ev.call || 'function()', MAX_CALL_CHARS);
  // function-io-stdin: the typed answers, clipped on their own so a long call never hides them.
  if (typeof ev.typed === 'string') { call += ' typing ' + clip(ev.typed, MAX_TYPED_CHARS); }
  var mark = ev.passed ? PASS_MARK : FAIL_MARK;
  var wantsReturn = ev.mode !== 'prints';
  var wantsPrints = ev.mode === 'prints' || ev.mode === 'both';
  var expected = wantsReturn && wantsPrints
    ? v(ev.expected) + ' and printed ' + v(ev.expectedPrinted)
    : wantsPrints ? v(ev.expectedPrinted) : v(ev.expected);

  if (ev.outcome === 'missing') {
    return call + ' could not be run ' + mark;
  }
  if (ev.outcome === 'crash') {
    return call + ' crashed with a ' + clip(ev.errorType || 'error', MAX_VALUE_CHARS) +
      (wantsReturn ? ' — expected ' + expected : '') + ' ' + mark;
  }
  if (ev.outcome === 'uncomparable') {
    return call + ' returned something that can’t be compared — expected ' + expected + ' ' + mark;
  }
  var got;
  if (wantsReturn && wantsPrints) {
    got = 'returned ' + v(ev.got) + ' and printed ' + v(ev.gotPrinted);
  } else if (wantsPrints) {
    got = 'printed ' + v(ev.gotPrinted);
  } else {
    got = 'returned ' + v(ev.got);
  }
  return call + ' ' + got + ' — expected ' + expected + ' ' + mark;
}

// The whole readout for one function-io result: a heading, up to MAX_CASES lines and a
// "+N more" tail. Returns null when there is nothing to show (no evidence — a skipped
// checkpoint, or a missing / misnamed function, whose own message already says what happened).
//
// When the cases do not all fit, FAILING cases are listed first (authoring order kept within
// each group), so a cap never hides the one line that explains a fail.
export function functionEvidence(result, opts) {
  var max = (opts && opts.max) || MAX_CASES;
  var list = result && Array.isArray(result.evidence) ? result.evidence : [];
  if (!list.length) { return null; }
  var ordered = list.length > max
    ? list.filter(function (e) { return !e.passed; }).concat(list.filter(function (e) { return e.passed; }))
    : list.slice();
  var shown = ordered.slice(0, max);
  var mode = list[0] && list[0].mode;
  var heading = mode === 'prints'
    ? 'The checker called your function and read what it printed:'
    : mode === 'both'
      ? 'The checker called your function and read what it returned and printed:'
      : 'The checker called your function and read what it returned:';
  return {
    heading: heading,
    lines: shown.map(function (e) { return { text: evidenceLine(e), passed: !!e.passed }; }),
    more: list.length - shown.length
  };
}
